import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Filter,
  ClipboardCheck,
  Clock,
  Users,
  Tag,
  Download,
  Eye,
  SlidersHorizontal,
  CheckSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { sopData } from '../../data/sopData';
import ChecklistCard from './ChecklistCard';
import ViewToggle from '../../../../components/shared/ViewToggle';
import { exportChecklistToPDF } from '../../../../utils/pdfExport';

const handleDownload = (checklist) => {
  const doc = exportChecklistToPDF(checklist);
  doc.save(`${checklist.title.toLowerCase().replace(/\s+/g, '-')}-checklist.pdf`);
};

const ChecklistListItem = ({ checklist }) => {
  const completedItems = checklist.items.filter(item => item.completed).length;
  const totalItems = checklist.items.length;
  const completionPercentage = Math.round((completedItems / totalItems) * 100) || 0;

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${
            checklist.frequency === 'daily' ? 'bg-green-50' :
            checklist.frequency === 'weekly' ? 'bg-blue-50' :
            checklist.frequency === 'monthly' ? 'bg-purple-50' :
            'bg-gray-50'
          }`}>
            <ClipboardCheck className={`w-5 h-5 ${
              checklist.frequency === 'daily' ? 'text-green-600' :
              checklist.frequency === 'weekly' ? 'text-blue-600' :
              checklist.frequency === 'monthly' ? 'text-purple-600' :
              'text-gray-600'
            }`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{checklist.title}</h3>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-sm text-gray-500">{checklist.category}</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{checklist.frequency}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {/* Progress */}
          <div className="flex items-center gap-3">
            <div className="w-32">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700">Progress</span>
                <span className="text-xs font-medium text-gray-700">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Items Preview */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CheckSquare className="w-4 h-4" />
            {completedItems}/{totalItems} Items
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDownload(checklist)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Download className="w-4 h-4" />
            </button>
            <Link
              to={`/sop/checklists/${checklist.id}`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Checklist
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChecklistsTab = () => {
  const [view, setView] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique frequencies
  const frequencies = ['all', ...new Set(sopData.checklists.map(checklist => checklist.frequency))];

  const filteredChecklists = sopData.checklists.filter(checklist => {
    const matchesSearch = checklist.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         checklist.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFrequency = selectedFrequency === 'all' || checklist.frequency === selectedFrequency;
    return matchesSearch && matchesFrequency;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Operational Checklists</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage standardized checklists for your operations
          </p>
        </div>
        <div className="flex items-center gap-4">
          <ViewToggle view={view} onViewChange={setView} />
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
          </button>
          <Link
            to="/sop/checklists/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Checklist
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search checklists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Frequency Filter */}
        <div className="flex overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex gap-2">
            {frequencies.map(frequency => (
              <button
                key={frequency}
                onClick={() => setSelectedFrequency(frequency)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedFrequency === frequency
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="whitespace-nowrap capitalize">
                  {frequency === 'all' ? 'All Frequencies' : frequency}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {view === 'grid' ? (
          <motion.div 
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredChecklists.map(checklist => (
              <ChecklistCard 
                key={checklist.id} 
                checklist={checklist}
                onDownload={handleDownload}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {filteredChecklists.map(checklist => (
              <ChecklistListItem key={checklist.id} checklist={checklist} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChecklistsTab; 